import { ApplicationRef, ChangeDetectorRef, Component, ComponentFactoryResolver, Injector, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { CoreService, DataService, DialogService, DspxStatusBarComponent, ElementType, FormNavigationService, SpinnerService } from 'dspx-core';
import { finalize, take, filter } from 'rxjs';
import { API_ENDPOINTS, API_ENDPOINT_KEYS } from '../../constants/api-endpoints.constants';
import { APPLICATION_IDS, APP_DATA_KEYS, DATA_GRID_ADD_NEW_POP_UPS, FIELD_VALIDATION_PATTERNS, POP_UP_CODES, POP_UP_TEXTS, POP_UP_TITLES } from '../../constants/app-data.constants';
import { VERSIONS } from '../../constants/templates.constants';
import { FormResponseSaveRequest } from '../../interfaces/form-response-save-request.interface';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { FormDataService } from '../../services/form-data.service';
import { BaseClassicDetailComponent } from '../base-classic-detail-component/base-classic-detail.component';
import { formatToUIDateTimeFormat } from '../../helpers/date-helper';
import { FormResponseSavedNotificationRequest } from 'src/app/interfaces/form-response-saved-notification-request.interface';
import { FormGroup } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { Form04HelperService } from '../../services/form04-helper.service';
import { Form08HelperService } from '../../services/form08-helper.service';
import { FormCommonHelperService } from '../../services/formCommon.service';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { NgRecaptcha3Service } from 'ng-recaptcha3';
import { Form48HelperService } from 'src/app/services/form48-helper.service';
import { ROUTE_CONSTANTS } from 'src/app/constants/route.constants';

@Component({
  selector: 'app-form-response',
  templateUrl: './form-response.component.html',
  styleUrl: './form-response.component.scss'
})
export class FormResponseComponent extends BaseClassicDetailComponent implements OnInit, OnDestroy {
  id = '';
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getFormSubmissionConfig;
  applicationAndTemplateId: string = '';
  applicationId: string = '';
  templateId: number = -1;
  versionNo: number = VERSIONS.latest;
  activeDialog: string;
  editing = false;
  referenceCode: string;
  submissionDate: string;
  dataGridAddNewButtonClickedEvent;
  
  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected errorService: ErrorService,
    protected coreService: CoreService,
    protected dataService: DataService,
    protected dialogService: DialogService,
    protected spinnerService: SpinnerService,
    protected appDataService: AppDataService,
    protected formDataService: FormDataService,
    private injector: Injector,
    private applicationRef: ApplicationRef,
    private resolver: ComponentFactoryResolver,
    private cdr: ChangeDetectorRef,
    private form04HelperService: Form04HelperService,
    private form08HelperService: Form08HelperService,
    private form48HelperService: Form48HelperService,
    private formCommonHelperService: FormCommonHelperService,
    private formNavigationService: FormNavigationService,
    private recaptchaService: NgRecaptcha3Service
  ) {
    super(router, appDataService);
  }

  ngOnInit(): void {
    this.registerSubscription(
      this.route.paramMap.subscribe((paramMap: ParamMap) => {
        const id = paramMap.get('id');
        if (id) {
          // Editing an existing form response.
          this.id = id;
          this.formServiceEndpoint = API_ENDPOINT_KEYS.getFormSubmissionConfigAndResponse;
          this.applicationAndTemplateId = `${this.templateId}`;
          this.editing = true;
        }
        else {
          // New form response.
          const applicationId = paramMap.get('applicationId');
          if (applicationId) {
            this.applicationId = applicationId;
          }
          const templateId = paramMap.get('templateId');
          if (templateId) {
            this.templateId = templateId as unknown as number;
          }
          this.applicationAndTemplateId = `${this.applicationId}/${this.templateId}`;

          this.resetDirtyFlagAfterLoading();

        }
      })
    );

    this.coreService.conditionFunctions['isEditing'] = () => this.editing;

    this.coreService.conditionFunctions = {
      isReadOnly: () => this.formCommonHelperService.isReadOnly(),

      // Survey 08 - Application for a licence or permit
      // basicInfo functions --------------------------------------------------------------
      s08hsp: (data: any) => this.form08HelperService.isHsp(data),
      s08nonHSPpermit: (data: any) => this.form08HelperService.isNonHspPermit(data),
      s08licenceManufacturing: (data: any) => this.form08HelperService.isLicenceManufacturing(data),
      s08licenceIndent: (data: any) => this.form08HelperService.isLicenceIndent(data),
      s08licenceNonManufactNonIndent: (data: any) => this.form08HelperService.isLicenceNonManufactNonIndent(data),

      // Health Service Use -----------------------------------------------------------------
      s08hSPdermalInjectables: (data: any) => this.form08HelperService.isHspDermalInjectables(data),
      s08hSPdermalInjectablesNothingElse: (data: any) => this.form08HelperService.isHspDermalInjectablesNothingElse(data),
      s08vetHSP: (data: any) => this.form08HelperService.isVetHsp(data),
      s08agedCareHSP: (data: any) => this.form08HelperService.isAgedCareHsp(data),
      s08agedCareHspNothingElse: (data: any) => this.form08HelperService.isAgedCareHspNothingElse(data),
      s08hospitalHSP: (data: any) => this.form08HelperService.isHospitalHSP(data),
      s08dayProcedureCentreHSP: (data: any) => this.form08HelperService.isDayProcedureCentreHsp(data),

      // Substances ------------------------------------------------------------------------
      s08s2Addition: (data: any) => this.form08HelperService.isSchedule2Substance(data),
      s08s3Addition: (data: any) => this.form08HelperService.isSchedule3Substance(data),
      s08s4Addition: (data: any) => this.form08HelperService.isSchedule4Substance(data),
      s08s4ddAddition: (data: any) => this.form08HelperService.isSchedule4DDSubstance(data),
      s08s7Addition: (data: any) => this.form08HelperService.isSchedule7Substance(data),
      s08s7lrAddition: (data: any) => this.form08HelperService.isSchedule7LRSubstance(data),
      s08s8Addition: (data: any) => this.form08HelperService.isSchedule8Substance(data),
      s08s9Addition: (data: any) => this.form08HelperService.isSchedule9Substance(data),
      s08cannabisAddition: (data: any) => this.form08HelperService.isCannabisAddition(data),
      s08nitrousAddition: (data: any) => this.form08HelperService.isNitrousAddition(data),

      s08anyIndividualSubstance8: (data: any) => this.form08HelperService.anyIndividualSubstance8(data),
      s08anyIndividualSubstance9: (data: any) => this.form08HelperService.anyIndividualSubstance9(data),
      s08anyIndividualSubstanceCannabis: (data: any) => this.form08HelperService.anyIndividualSubstanceCannabis(data),

      s08s2: (data: any) => this.form08HelperService.isSchedule2SubstanceOrGroup(data),
      s08s3: (data: any) => this.form08HelperService.isSchedule3SubstanceOrGroup(data),
      s08s4: (data: any) => this.form08HelperService.isSchedule4SubstanceOrGroup(data),
      s08s4dd: (data: any) => this.form08HelperService.isSchedule4DDSubstanceOrGroup(data),
      s08s7: (data: any) => this.form08HelperService.isSchedule7SubstanceOrGroup(data),
      s08s7lr: (data: any) => this.form08HelperService.isSchedule7LRSubstanceOrGroup(data),
      s08s8: (data: any) => this.form08HelperService.isSchedule8SubstanceOrGroup(data),
      s08s9: (data: any) => this.form08HelperService.isSchedule9SubstanceOrGroup(data),
      s08therapeuticGoods: (data: any) => this.form08HelperService.isTherapeuticGoods(data),

      // Misc -------------------------------------------------------------------------------
      s08noSchedule8or9Substance: (data: any) => this.form08HelperService.noSchedule8or9Substance(data),
      s08licencePermitTypeTopOfForm: (data: any) => this.form08HelperService.licencePermitTypeTopOfForm(data),
      s08tradingNameTopOfForm: (data: any) => this.form08HelperService.tradingNameTopOfForm(data),
      s08s8or9LicenceTopOfForm: (data: any) => this.form08HelperService.s8or9LicenceTopOfForm(data),
      s08feeTopOfForm: (data: any) => this.form08HelperService.feeTopOfForm(data),
      s08licPerLast6DigitsfeeTopOfForm: (data: any) => this.form08HelperService.licPerLast6DigitsfeeTopOfForm(data),
      s08maxHoldingIndividualTotal: (data: any) => this.form08HelperService.maxHoldingIndividualTotal(data),
      s08cannabisAddition2: (data: any) => this.form08HelperService.cannabisAddition2(data),
      s08nitrousAddition2: (data: any) => this.form08HelperService.nitrousAddition2(data),
      s08nonHSPWithAdmin: (data: any) => this.form08HelperService.nonHSPWithAdmin(data),
      s08totalYearsExperienceValidation: (data: any) => this.form08HelperService.totalYearsExperienceValidation(data),
      s08totalYearsExperience: (data: any) => this.form08HelperService.totalYearsExperience(data),
      s08hpAdminInitiateSelected: (data: any) => this.form08HelperService.hpAdminInitiateSelected(data),
      s08hpSupplySelected: (data: any) => this.form08HelperService.hpSupplySelected(data),
      s08hpVerbalOrderSelected: (data: any) => this.form08HelperService.hpVerbalOrderSelected(data),
      s08nitrousAlone: (data: any) => this.form08HelperService.nitrousAlone(data),
      s08fee: (data: any) => this.form08HelperService.fee(data),
      s08submitPresentationNotSavePresentation: (data: any) => this.form08HelperService.submitPresentationNotSavePresentation(data),


      // Survey 04 - Application for a licence or permit
      // basicInfo functions --------------------------------------------------------------
      s04hsp: (data: any) => this.form04HelperService.isHsp(data),
      s04nonHSPpermit: (data: any) => this.form04HelperService.isNonHspPermit(data),
      s04licenceManufacturing: (data: any) => this.form04HelperService.isLicenceManufacturing(data),
      s04licenceIndent: (data: any) => this.form04HelperService.isLicenceIndent(data),
      s04licenceNonManufactNonIndent: (data: any) => this.form04HelperService.isLicenceNonManufactNonIndent(data),

      s04vetHsp: (data: any) => this.form04HelperService.isVetHsp(data),

      s04s2Addition: (data: any) => this.form04HelperService.isSchedule2Substance(data),
      s04s3Addition: (data: any) => this.form04HelperService.isSchedule3Substance(data),
      s04s4Addition: (data: any) => this.form04HelperService.isSchedule4Substance(data),
      s04s4ddAddition: (data: any) => this.form04HelperService.isSchedule4DDSubstance(data),
      s04s7Addition: (data: any) => this.form04HelperService.isSchedule7Substance(data),
      s04s7lrAddition: (data: any) => this.form04HelperService.isSchedule7LRSubstance(data),
      s04s8Addition: (data: any) => this.form04HelperService.isSchedule8Substance(data),
      s04s9Addition: (data: any) => this.form04HelperService.isSchedule9Substance(data),
      s04cannabisAddition: (data: any) => this.form04HelperService.isCannabisAddition(data),
      s04nitrousAddition: (data: any) => this.form04HelperService.isNitrousAddition(data),

      s04anyIndividualSubstance8: (data: any) => this.form04HelperService.anyIndividualSubstance8(data),
      s04anyIndividualSubstance9: (data: any) => this.form04HelperService.anyIndividualSubstance9(data),
      s04anyIndividualSubstanceCannabis: (data: any) => this.form04HelperService.anyIndividualSubstanceCannabis(data),

      s04s2: (data: any) => this.form04HelperService.isSchedule2SubstanceOrGroup(data),
      s04s3: (data: any) => this.form04HelperService.isSchedule3SubstanceOrGroup(data),
      s04s4: (data: any) => this.form04HelperService.isSchedule4SubstanceOrGroup(data),
      s04s4dd: (data: any) => this.form04HelperService.isSchedule4DDSubstanceOrGroup(data),
      s04s7: (data: any) => this.form04HelperService.isSchedule7SubstanceOrGroup(data),
      s04s7lr: (data: any) => this.form04HelperService.isSchedule7LRSubstanceOrGroup(data),
      s04s8: (data: any) => this.form04HelperService.isSchedule8SubstanceOrGroup(data),
      s04s9: (data: any) => this.form04HelperService.isSchedule9SubstanceOrGroup(data),
      s04therapeuticGoods: (data: any) => this.form04HelperService.isTherapeuticGoods(data),
      s04nitrousAlone: (data: any) => this.form04HelperService.nitrousAlone(data),
      s04nonHSPWithAdmin: (data: any) => this.form04HelperService.nonHspWithAdmin(data),

      s04hasFee: (data: any) => this.form04HelperService.hasFee(data),

    }
  };

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoaded(event: any) {
    this.id = event.id;
    this.templateId = event.template;
    this.versionNo = event.versionNumber;
    this.referenceCode = event.referenceCode;
    this.submissionDate = event.submissionDate;

    this.definePagePopups();

    if (event.applicationId != null) {
      this.applicationId = event.applicationId;
    }
    else{
      this.promptReferenceNumber();
    }
    
    setTimeout(() => {
      const tabPanelElement = document.querySelector('p-tabpanel');
      const statusBarFactory = this.resolver.resolveComponentFactory(DspxStatusBarComponent);
      let statusBarComponentRef = statusBarFactory.create(this.injector);
      statusBarComponentRef.instance.content = `Reference Code: ${this.referenceCode}`;
      this.applicationRef.attachView(statusBarComponentRef.hostView);
      tabPanelElement.insertBefore((statusBarComponentRef.hostView as any).rootNodes[0], tabPanelElement.firstChild);
      statusBarComponentRef.changeDetectorRef.detectChanges();
    });

    if (this.baseForm && this.baseForm.model) {
      this.appDataService.setAppData(APP_DATA_KEYS.previousModel, JSON.parse(JSON.stringify(this.baseForm.model)));
    }

    this.setFormIdentifierForFileUploadFields(this.baseForm.fields);

    if(this.shouldGeneratePDF()) {
      setTimeout(() => {
        this.moveToLastPageAndDownloadFormSummaryPDF();
      }, 300);
    }
    
    const recaptchaSiteKey = document.getElementById('googleRecaptchaSiteKey').attributes['key'].value;
    this.recaptchaService.init(recaptchaSiteKey);

    setTimeout(() => {
      this.registerCustomFormListners(event);
    });
  }

  setFormIdentifierForFileUploadFields = (fields: any[]) => {
    if (!fields) return;
    for (const field of fields) {
      if (field.type === 'file') {
        this.coreService.fileUploadFormIdentifiers[field.key.toString()] = this.baseForm.formIdentifier;
      }
      if (field.fieldGroup || field.fieldArray) {
        this.setFormIdentifierForFileUploadFields(field.fieldGroup ?? field.fieldArray.fieldGroup);
      }
    }
  };

  private definePagePopups() {
    const emailQuestion = {
      key: 'email',
      type: 'input',
      templateOptions: {
        label: 'Email',
        required: true,
        type: 'email',
        pattern: FIELD_VALIDATION_PATTERNS.email
      },
      validation: {
        messages: {
          required: POP_UP_TEXTS.formResponse.emailPrompt.requiredValidationMessage,
          pattern: POP_UP_TEXTS.formResponse.emailPrompt.emailPatternValidationMessage
        }
      }
    };
    this.dialogService.setPopups([
      {
        "code": POP_UP_CODES.formResponse.saveSuccessful,
        "title": POP_UP_TITLES.formResponse.saveSuccessful,
        "text": POP_UP_TEXTS.formResponse.saveSuccessful,
        "type": "info",
        "okButtonText": "OK",
        "cancelButtonText": "Cancel",
        "severity": "info",
        "questions": [
          emailQuestion
        ]
      },
      {
        "code": POP_UP_CODES.formResponse.submissionSuccessful,
        "title": POP_UP_TITLES.formResponse.submissionSuccessful,
        "text": POP_UP_TEXTS.formResponse.submissionSuccessful,
        "type": "info",
        "okButtonText": "OK",
        "cancelButtonText": "",
        "severity": "info",
        "questions": [
          emailQuestion
        ]
      },
      {
        "code": POP_UP_CODES.formResponse.promptForReferenceCode,
        "title": POP_UP_TITLES.formResponse.promptForReferenceCode,
        "text": POP_UP_TEXTS.formResponse.promptForReferenceCode.description,
        "type": "info",
        "okButtonText": POP_UP_TEXTS.formResponse.promptForReferenceCode.okButtonText,
        "cancelButtonText": POP_UP_TEXTS.formResponse.promptForReferenceCode.cancelButtonText,
        "severity": "info",
        "questions": [
          {
            key: 'referenceCode',
            type: 'input',
            templateOptions: {
              label: POP_UP_TEXTS.formResponse.promptForReferenceCode.referenceCodeQutstionLabel,
              required: true,
              type: 'text'
            },
            validation: {
              messages: {
                required: POP_UP_TEXTS.formResponse.promptForReferenceCode.requiredValidationMessage
              }
            }
          }
        ]
      },
      {
        "code": POP_UP_CODES.formResponse.addRadionuclideSourceNotification,
        "title": POP_UP_TITLES.formResponse.addRadionuclideSourceNotification,
        "text": null,
        "type": "info",
        "okButtonText": POP_UP_TEXTS.formResponse.addDataToDataGrid.okButtonText,
        "cancelButtonText": POP_UP_TEXTS.formResponse.addDataToDataGrid.cancelButtonText,
        "severity": "info",
        "questions": [
          {
            key: 'radionuclide',
            type: 'input',
            templateOptions: {
              label: 'Radionuclide',
              required: true,
              type: 'text'
            },
            validation: {
              messages: {
                required: 'Radionuclide is required.'
              }
            }
          },
          {
            key: 'activity',
            type: 'input',
            templateOptions: {
              label: 'Activity',
              required: true,
              type: 'number'
            },
            validation: {
              messages: {
                required: 'Activity is required.'
              }
            }
          },
          {
            key: 'unit',
            type: 'select',
            templateOptions: {
              label: 'Unit',
              required: true,
              options: [
                { label: 'Bq', value: 'Bq' },
                { label: 'kBq', value: 'kBq' },
                { label: 'MBq', value: 'MBq' },
                { label: 'GBq', value: 'GBq' },
                { label: 'TBq', value: 'TBq' }
              ]
            },
            validation: {
              messages: {
                required: 'Unit is required.'
              }
            }
          },
          {
            key: 'measuredDate',
            type: 'input',
            templateOptions: {
              label: 'Date measured',
              required: true,
              type: 'date'
            },
            validation: {
              messages: {
                required: 'Date measured is required.'
              }
            }
          }
        ]
      },
      {
        "code": POP_UP_CODES.formResponse.addPermittedRadionuclideForTransferManagementLicence,
        "title": POP_UP_TITLES.formResponse.addPermittedRadionuclideForTransferManagementLicence,
        "text": null,
        "type": "info",
        "okButtonText": POP_UP_TEXTS.formResponse.addDataToDataGrid.okButtonText,
        "cancelButtonText": POP_UP_TEXTS.formResponse.addDataToDataGrid.cancelButtonText,
        "severity": "info",
        "questions": [
          {
            key: 'radionuclide',
            type: 'input',
            templateOptions: {
              label: 'Radionuclide',
              required: true,
              type: 'text'
            },
            validation: {
              messages: {
                required: 'Radionuclide is required.'
              }
            }
          },
          {
            key: 'maximumActivity',
            type: 'input',
            templateOptions: {
              label: 'Maximum activity',
              required: true,
              type: 'number'
            },
            validation: {
              messages: {
                required: 'Maximum activity is required.'
              }
            }
          },
          {
            key: 'unit',
            type: 'select',
            className: 'dropdown-radionuclide-unit',
            templateOptions: {
              label: 'Unit',
              required: true,
              options: [
                { label: 'Bq', value: 'Bq' },
                { label: 'kBq', value: 'kBq' },
                { label: 'MBq', value: 'MBq' },
                { label: 'GBq', value: 'GBq' },
                { label: 'TBq', value: 'TBq' }
              ]
            },
            validation: {
              messages: {
                required: 'Unit is required.'
              }
            }
          }
        ]
      }
    ]);

    // [621995] Fix issue where validation doesn't run correctly on first dialog submit
    this.dialogService.dialogRequested().subscribe(dialog => {
      if(dialog?.popup?.code == POP_UP_CODES.formResponse.submissionSuccessful || dialog?.popup?.code == POP_UP_CODES.formResponse.saveSuccessful){
        setTimeout(() => {
          dialog?.popup?.questions?.[0]?.form?.reset();
        });
      }
    });
  }

  save(event: any) {
    this.recaptchaService.getToken().then((token: string) => {
    const saveDataRequest: FormResponseSaveRequest = {
      applicationId: this.applicationId,
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.id,
      response: event.model,
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: event.isSubmit,
      referenceCode: this.referenceCode,
      formIdentifier: this.baseForm.formIdentifier,
      recaptchaToken: token
    }
    if (!event.isFormValid && event.isSubmit) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showDialog(POP_UP_CODES.common.validationError);
      }
      else {
        this.saveData(saveDataRequest, event.closeFormAfterSaving);
      }
    }
    else {
      this.saveData(saveDataRequest, event.closeFormAfterSaving);
    }
    });
  }

  dialogButton_saveSuccessful_Clicked(event: any): void {
    //this.navigateBackToDashboard(true);
    this.navigateToSuccessfulSubmission(true);
  }

  cancel(event: any) {
    this.navigateBackToDashboard();
  }

  modelChanged(event: any) {
    switch (this.applicationId) {
      case APPLICATION_IDS.s04ApplicationForChangeLicenceOrPermit:
        switch (this.templateId) {
          case 1:
            this.form04HelperService.calculateYearsExperience(this.baseForm);
            this.form04HelperService.calculateTotalMilligrams(this.baseForm);
            break;
        }
        break;

      case APPLICATION_IDS.s08ApplicationForALicenceOrPermit:
        switch (this.templateId) {
          case 1:
            this.form08HelperService.calculateFee(this.baseForm);
            this.form08HelperService.updatePersonDetails(this.baseForm);
            this.form08HelperService.calculateTotalMilligrams(this.baseForm);
            this.form08HelperService.calculateYearsExperience(this.baseForm);
            this.form08HelperService.updateSubmissionDetails(this.baseForm);
            break;
        }
        break;
    }
  }

  actionButtonClicked(event){
    if(event.event.type == 'new'){
      this.activeDialog = DATA_GRID_ADD_NEW_POP_UPS.popupCodeMapping[event?.field?.props?.dashboardOptions?.allowAddFunctionName];
      if(this.activeDialog != null){
        this.dataGridAddNewButtonClickedEvent = event;
        this.dialogService.showPopup(this.activeDialog);
      }
    }
    else if(event.event.type == 'delete'){
      this.dataGridAddNewButtonClickedEvent = event;
      const parentModel = this.dataGridAddNewButtonClickedEvent.field.parent?.model;
      if (parentModel && this.dataGridAddNewButtonClickedEvent?.field?.key && parentModel[this.dataGridAddNewButtonClickedEvent.field.key] != null) {
          parentModel[this.dataGridAddNewButtonClickedEvent.field.key] = parentModel[this.dataGridAddNewButtonClickedEvent.field.key].filter(x => x.id != event.event.data.id);
          (this.dataGridAddNewButtonClickedEvent.field.form as FormGroup).get(this.dataGridAddNewButtonClickedEvent.field.key).reset();
          this.cdr.detectChanges();
      }
    }
  }

  private navigateBackToDashboard(ignoreUnsavedChanges = false): void {
    this.navigate(['/'], ignoreUnsavedChanges);
  }

  private navigateToSuccessfulSubmission(ignoreUnsavedChanges = false): void{
    this.navigate([ROUTE_CONSTANTS.formSubmitted], ignoreUnsavedChanges);
  }

  private saveData(request: FormResponseSaveRequest, closeFormAfterSaving = false) {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.dataService.saveData(API_ENDPOINTS.formResponse.save, request)
        .pipe(take(1), this.errorService.errorHandler())
        .subscribe(data => {
          if (data?.status === 'Success') {
            this.spinnerService.hideSpinner();
            this.id = data.id;
            this.baseForm.lastUpdatedTime = data.allocatedLastUpdatedTime;
            if (request.isSubmit) {
              this.activeDialog = POP_UP_CODES.formResponse.submissionSuccessful;
              this.dialogService.showDialog(this.activeDialog, { isSubmit: true },
                { referenceCode: this.referenceCode }, true);
            }
            else if (closeFormAfterSaving) {
              this.activeDialog = POP_UP_CODES.formResponse.saveSuccessful;
              this.dialogService.showDialog(this.activeDialog, { isSubmit: false },
                { expiryDateTime: formatToUIDateTimeFormat(data.draftResponseExpirationDate), referenceCode: this.referenceCode }, true);
            }
          }
        })
    );
  }

  dialogButton_formResponseSaveSuccessful_Clicked(event) {
    this.sendFormResponseSavedOrSubmittedNotificationAndNavigateToDashboard(event);
  }

  dialogButton_formResponseSubmissionSuccessful_Clicked(event) {
    this.sendFormResponseSavedOrSubmittedNotificationAndNavigateToDashboard(event);
  }

  dialogButton_addRadionuclideSourceNotification_Clicked(event) {
    this.addEntryToDataGrid(event)
  }

  dialogButton_addPermittedRadionuclideForTransferManagementLicence_Clicked(event) {
    this.addEntryToDataGrid(event)
  }

  private addEntryToDataGrid(event) {

    var dataRow = {
      ...event,
      id: uuidv4(),
      actions: {
        delete: true
      }
    };

    const parentModel = this.dataGridAddNewButtonClickedEvent.field.parent?.model;
    if (parentModel && this.dataGridAddNewButtonClickedEvent?.field?.key && parentModel[this.dataGridAddNewButtonClickedEvent.field.key] == null) {
        parentModel[this.dataGridAddNewButtonClickedEvent.field.key] = [];
    }
    this.dataGridAddNewButtonClickedEvent.field.model.push(dataRow);
    (this.dataGridAddNewButtonClickedEvent.field.form as FormGroup).get(this.dataGridAddNewButtonClickedEvent.field.key).reset();
    this.cdr.detectChanges();
  }

  dialogButton_promptForReferenceCode_Clicked(event) {
    this.navigate(['/form-response', event.referenceCode], true);
  }

  private sendFormResponseSavedOrSubmittedNotificationAndNavigateToDashboard(event: any) {
    const savedNotificationRequest: FormResponseSavedNotificationRequest = {
      applicationId: this.applicationId,
      referenceCode: this.referenceCode,
      email: event.email,
      isSubmit: event.isSubmit
    };
    this.appDataService.post(API_ENDPOINTS.formResponse.sendResponseSavedNotification, savedNotificationRequest)
      .pipe(
        take(1),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner()))
      .subscribe(() => {
        //this.navigateBackToDashboard(true);
        this.navigateToSuccessfulSubmission(true);
      });
  }

  private promptReferenceNumber() {
    this.activeDialog = POP_UP_CODES.formResponse.promptForReferenceCode;
    this.dialogService.showDialog(this.activeDialog);
  }

  private shouldGeneratePDF() {
    return this.route.snapshot.queryParamMap.get('pdf') == 'true';
  }

  private moveToLastPageAndDownloadFormSummaryPDF() {
    const { lastTabNumber, lastPageNumber } = this.getLastTabAndLastPageNumbers();
    
    let container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '-100000px';
    container.style.left = '0';
    container.style.width = 'auto';
    container.style.height = 'auto';
    container.style.zIndex = '-1';
    
    let currentPage = 1;
    
    const launchPage = () => {
      if (currentPage <= lastPageNumber){
        this.formNavigationService.setActiveNumber(ElementType.TAB, lastTabNumber);
        this.formNavigationService.setActiveNumber(ElementType.PAGE, currentPage);

        const tabsElement = document.querySelector('dspx-tabs')?.parentElement as HTMLElement;
        if (!tabsElement) return;

        const printElement = tabsElement.cloneNode(true) as HTMLElement;

        // Append header and footer 
        const header = document.querySelector('dspx-header')?.cloneNode(true);
        const footer = document.querySelector('dspx-footer')?.cloneNode(true);
        const statusBar = document.querySelector('dspx-status-bar')?.cloneNode(true) as HTMLElement;

        if(statusBar){
          const statusBarTextContainer = statusBar.querySelector('.status-bar');
          if (statusBarTextContainer) {
            statusBarTextContainer.textContent += ` Submission Date: ${ this.submissionDate }`;
          }
        }

        if (header) {
          const spacer = document.createElement('div');
          spacer.style.height = '40px';
          printElement.insertBefore(header, printElement.firstChild);
          printElement.insertBefore(spacer, printElement.firstChild?.nextSibling ?? null);
          printElement.insertBefore(statusBar, printElement.firstChild?.nextSibling?.nextSibling ?? null);
        }

        if (footer) {
          printElement.appendChild(footer);
        }
        container.appendChild(printElement);
        currentPage++;
        setTimeout(launchPage);
      }
      else{
        //navigate back to first page to avoid any user confusion before printing pdf
        this.formNavigationService.setActiveNumber(ElementType.TAB, lastTabNumber);
        this.formNavigationService.setActiveNumber(ElementType.PAGE, 1);
        document.body.appendChild(container);
        html2canvas(container).then(canvas => {
          
          const img = new Image();
          img.src = canvas.toDataURL('image/png');
          img.onload = () => {
            const pdf = new jsPDF('l', 'mm', 'a4',true);
            const padding = 10;
            // const pageWidth = pdf.internal.pageSize.getWidth() - 2 * padding;
            // const pageHeight = pdf.internal.pageSize.getHeight() - 2 * padding;
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight() - 1;

            const pxToMm = (px: number) => px * 0.264583;
            const imgWidthMm = pxToMm(canvas.width);
            const imgHeightMm = pxToMm(canvas.height);
            const scale = pageWidth / imgWidthMm;
            const scaledHeight = imgHeightMm * scale;

            let position = 0;
            let heightLeft = scaledHeight;

            pdf.addImage(img, 'PNG', 0, position, pageWidth, scaledHeight, undefined, 'FAST');
            heightLeft -= pageHeight;

            while (heightLeft > 0) {
              position -= pageHeight;
              pdf.addPage();
              pdf.addImage(img, 'PNG', 0, position, pageWidth, scaledHeight, undefined, 'FAST');
              heightLeft -= pageHeight;
            }

            const timestamp = new Date().toISOString()
              .replace(/T/, '_')
              .replace(/:/g, '-')
              .replace(/\..+/, '');
            const fileName = `${this.referenceCode}_${timestamp}.pdf`;
            pdf.save(fileName);
            document.body.removeChild(container);
          };
        });
      }
    }

    launchPage();
  }

  private getLastTabAndLastPageNumbers() {
    const tabs = this.baseForm.fields[0].fieldGroup[0].fieldGroup;
    const pagesInLastTab = tabs[tabs.length - 1].fieldGroup;

    const lastTabNumber = tabs.length;
    // if the last page on last tab is of type form-summary then ignore it
    const lastPageNumber = pagesInLastTab[pagesInLastTab.length - 1]?.type === 'summary-page' ? pagesInLastTab.length - 1 : pagesInLastTab.length;
    return { lastTabNumber, lastPageNumber };
  }

  private generateAndDownloadFormSummaryPDF(): void {
    //const formSummary = document.querySelector('dspx-form-summary') as HTMLElement;
    const formSummary = document.querySelector('dspx-tabs')?.parentElement as HTMLElement;
    if (!formSummary) return;

    // Clone to offscreen container with flexible height and width
    const clone = formSummary.cloneNode(true) as HTMLElement;

    // Append header and footer 
    const header = document.querySelector('dspx-header')?.cloneNode(true);
    const footer = document.querySelector('dspx-footer')?.cloneNode(true);
    const statusBar = document.querySelector('dspx-status-bar')?.cloneNode(true) as HTMLElement;

    if(statusBar){
      const statusBarTextContainer = statusBar.querySelector('.status-bar');
      if (statusBarTextContainer) {
        statusBarTextContainer.textContent += ` Submission Date: ${ this.submissionDate }`;
      }
    }

    if (header) {
      const spacer = document.createElement('div');
      spacer.style.height = '40px';
      clone.insertBefore(header, clone.firstChild);
      clone.insertBefore(spacer, clone.firstChild?.nextSibling ?? null);
      clone.insertBefore(statusBar, clone.firstChild?.nextSibling?.nextSibling ?? null);
    }

    if (footer) {
      clone.appendChild(footer);
    }
  }

  private registerCustomFormListners(event: any) {
    switch (this.applicationId) {
      case APPLICATION_IDS.s48Schedule4ApplicationForRenewal:
        switch (this.templateId) {
          case 1:
            this.form48HelperService.registerListeners(this.baseForm);
            break;
        }
        break;
    }
  }

}
